/* eslint-disable local-rules/no-exported-domain-types-outside-domain */

/**
 * Tweaks v2 — the cross-stage schema contract (schema version 3, the
 * regression-repair shape ruled by REBUILD-PLAN.md).
 *
 * This file is the single source of truth for the shapes shared by:
 *   - the `useTweaks` hook + `TweaksProvider` (this folder),
 *   - the /dev/tweaks full-screen page,
 *   - the agent-facing CLI.
 *
 * Store layout: ONE JSON file per context at
 *   `apps/fe/src/app/dev/tweaks/contexts/<contextName>.json`
 * holding a `TweakContextFile`. The browser persists through the dev-server
 * write seam (`/dev/tweaks/context-state`); agents edit the same files
 * directly with re-read-before-write discipline.
 *
 * Identity model: names ARE identity. A mark is identified by
 * `<context> / <mark name>`; an option by `<context> / <mark> / <option name>`.
 * Option leaf names are unique across the whole mark (they are the hook's
 * flat result keys), so a leaf's NAME is its stable path identity however
 * deep its group nesting goes. An optional display `label` may ride beside
 * the name — editing a label never changes identity.
 *
 * The selection model (census Part C, restored):
 *   - `selectedOptionName` is the ONE current selection per mark — REQUIRED,
 *     never null, defaulting to the `existing` sentinel. It alone drives the
 *     returned booleans; exactly one option is current at every moment,
 *     including on a brand-new mark. Preview does not exist.
 *   - `decisionOptionName` is the separate durable decision (v1's `locked`
 *     semantic — "bake this"). It keys the four-section lifecycle
 *     (active / seen / picked / fulfilled) and NEVER drives rendering.
 *   - `existing` is a first-class stored option (`existingOption`), first in
 *     the mark's choice group, able to own controls, a label, a comment, and
 *     scoped requests exactly like a generated option.
 *
 * Registration is the source of truth for the option TREE: the `useTweaks`
 * call creates marks and options in the JSON on first render, re-flags
 * orphans on every sync, and re-activates a mark whose registered option
 * membership changed (the handler touched the code). Registration NEVER
 * deletes anything and never touches selections, decisions, requests,
 * conversation, controls, or handler comments.
 *
 * This module is dependency-free and browser/node neutral on purpose: the CLI
 * imports it directly.
 */

export const TWEAKS_SCHEMA_VERSION = 3;

/** Context names are file basenames — keep them slug-shaped. */
export const TWEAKS_CONTEXT_NAME_PATTERN = /^[a-z0-9][a-z0-9-]*$/i;

/** Workflow state of one mark item — drives the four accordion sections of the
 *  /dev/tweaks page (active / seen / picked / fulfilled). Transitions key off
 *  the DECISION, never off the current selection:
 *  - newly created, or changed-after-seen (handler touched it) → Active;
 *  - mark-as-read → Seen;
 *  - decide on an option or mark-for-removal → Picked;
 *  - handler fulfilled the request → Fulfilled (a dispute appends to the
 *    conversation and returns the item to Active). */
export enum TweakItemState {
  Active = "active",
  Seen = "seen",
  Picked = "picked",
  Fulfilled = "fulfilled",
}

/** Who wrote a conversation entry or request. */
export enum TweakConversationAuthor {
  Roey = "roey",
  Handler = "handler",
}

/** Reserved option name: the existing rendering — the production
 *  fall-through. It is a STORED first-class option (`TweakMark.existingOption`)
 *  created by the store, never registered by a hook call; every returned
 *  boolean stays false while it is selected, so the consuming component's
 *  fall-through branch runs — the same path a production leak would take. */
export enum TweakOptionSentinel {
  Existing = "existing",
}

/** One entry in a mark's own conversation — dialogue history (owner notes,
 *  handler summaries, disputes) lives under the item, never globally. Handler
 *  entries display in the panel (a CLI `resolve <id> --comment` lands here).
 *  Typed actionable work is NOT conversation — it lives in `requests`. */
export interface TweakConversationEntry {
  from: TweakConversationAuthor;
  text: string;
  /** Epoch milliseconds. */
  at: number;
  /** Repo-relative image attachment paths. Empty array when none. */
  images: string[];
}

/** An attachment-capable comment value — EVERY comment a textfield writes
 *  carries image attachments (census F10); `string | null` comment fields do
 *  not exist in this schema. */
export interface TweakCommentText {
  text: string;
  /** Repo-relative image attachment paths; empty array when none. */
  images: string[];
}

// ---------------------------------------------------------------------------
// Controls (handler-authored per-option sliders/toggles)
// ---------------------------------------------------------------------------

/** Handler-authored per-option control kinds. */
export enum TweakControlKind {
  Slider = "slider",
  Toggle = "toggle",
}

interface TweakControlBase {
  /** Stable id, unique within its option — the key `controlValue` reads by. */
  id: string;
  /** Display label in the panel. */
  label: string;
  /** Locked by the user — the handler bakes the value and retires the
   *  control ("Locked — awaiting bake"). A decided option carries its CURRENT
   *  values even when unlocked. */
  locked: boolean;
  /** User/handler note attached to the control — attachment-capable (F10);
   *  null when none. */
  comment: TweakCommentText | null;
}

export interface TweakSliderControl extends TweakControlBase {
  kind: TweakControlKind.Slider;
  value: number;
  min: number;
  max: number;
  step: number;
}

export interface TweakToggleControl extends TweakControlBase {
  kind: TweakControlKind.Toggle;
  value: boolean;
}

export type TweakControl = TweakSliderControl | TweakToggleControl;

/** What `controlValue` hands the rendering component. */
export type TweakControlValue = number | boolean;

// ---------------------------------------------------------------------------
// Options — a recursive tree of leaves and groups (census F05)
// ---------------------------------------------------------------------------

/** One selectable option leaf. Its `name` is its stable identity and (for
 *  registered options) the key of its returned boolean. The store-created
 *  `existing` leaf uses the reserved sentinel name and returns no boolean. */
export interface TweakOption {
  name: string;
  /** Optional human display label beside the stable name (census F16).
   *  Editing it never changes identity; null renders the name itself. */
  label: string | null;
  /** true = the current registration no longer lists this option. Flagged by
   *  the store on sync, reported by the CLI, never auto-deleted here. */
  orphaned: boolean;
  /** The X on an option: queued for handler removal (fade in the panel). */
  markedForRemoval: boolean;
  /** Handler-authored note about this option, displayed in the panel.
   *  Attachment-capable (F10). */
  comment: TweakCommentText | null;
  /** Handler-authored controls. Values auto-persist as the user drags them;
   *  because the JSON always holds the current value, a decided option
   *  carries its control configuration with no extra snapshotting. */
  controls: TweakControl[];
}

export enum TweakOptionNodeKind {
  Option = "option",
  Group = "group",
}

export interface TweakOptionLeafNode {
  kind: TweakOptionNodeKind.Option;
  option: TweakOption;
}

/** A nested array in the registration spec — a variant sub-group. Groups nest
 *  to arbitrary depth; every leaf keeps independent metadata and its flat-
 *  unique name as stable path identity. */
export interface TweakOptionGroupNode {
  kind: TweakOptionNodeKind.Group;
  entries: TweakOptionNode[];
}

export type TweakOptionNode = TweakOptionLeafNode | TweakOptionGroupNode;

// ---------------------------------------------------------------------------
// Requests — first-class typed records (census F02, F03, F04, F06, F09)
// ---------------------------------------------------------------------------

/** What a request asks for. Issue = an issue/fix report (v1 "issue");
 *  Variants = a variant-generation request (v1 "tweak"); Save = capture an
 *  option into the collection or the system (v1 SaveRequest). */
export enum TweakRequestKind {
  Issue = "issue",
  Variants = "variants",
  Save = "save",
}

/** Open = unresolved work the handler still owes; Resolved = done. Collapsed
 *  mark rows count OPEN requests, never raw message totals (census F06). */
export enum TweakRequestStatus {
  Open = "open",
  Resolved = "resolved",
}

/** Where a save request lands (v1 SaveTarget). */
export enum TweakSaveTarget {
  Collection = "collection",
  System = "system",
}

/** Where the saved option's code lives, when known. The option identity
 *  itself (context / mark / option) is the record's position + scope. */
export interface TweakSaveProvenance {
  /** Repo-relative source file of the variant, when known; null otherwise. */
  sourceFile: string | null;
  /** Component name of the variant, when known; null otherwise. */
  component: string | null;
}

/** Fields every request record carries. A request's scope is
 *  {context, mark, option?}: context and mark are the record's position in
 *  the store; `option` names ONE option (including `existing`) or null for
 *  the whole mark — nothing about a request ever requires the mark-level
 *  decision to express it. */
interface TweakRequestBase {
  /** Stable id — the handle for edit/delete/status operations. */
  id: string;
  status: TweakRequestStatus;
  author: TweakConversationAuthor;
  /** Option name this request belongs to; null = the whole mark. */
  option: string | null;
  /** Epoch milliseconds — preserved across edits (audit). */
  createdAt: number;
  /** Epoch milliseconds — bumped by edits and status changes. */
  updatedAt: number;
}

export interface TweakIssueRequest extends TweakRequestBase {
  kind: TweakRequestKind.Issue;
  text: string;
  /** Repo-relative image attachment paths; empty array when none. */
  images: string[];
}

export interface TweakVariantsRequest extends TweakRequestBase {
  kind: TweakRequestKind.Variants;
  text: string;
  /** Repo-relative image attachment paths; empty array when none. */
  images: string[];
  /** Minimum variant count the kind implies (v1 `minVariants`). */
  minVariants: number;
}

export interface TweakSaveRequest extends TweakRequestBase {
  kind: TweakRequestKind.Save;
  /** A save always names its option — never mark-scoped. */
  option: string;
  target: TweakSaveTarget;
  /** Display name for the saved component (v1 SaveForm name). */
  displayName: string;
  description: string;
  /** Repo-relative image attachment paths; empty array when none. */
  images: string[];
  provenance: TweakSaveProvenance;
}

export type TweakRequest =
  | TweakIssueRequest
  | TweakVariantsRequest
  | TweakSaveRequest;

/** Default minimum variant count for a variants request (v1 default). */
export const TWEAKS_DEFAULT_MIN_VARIANTS = 2;

// ---------------------------------------------------------------------------
// Marks and the context file
// ---------------------------------------------------------------------------

/** One mark item — a decision point registered by a `useTweaks` call. */
export interface TweakMark {
  name: string;
  state: TweakItemState;
  /** true = no registration covers this mark any more (flagged by the CLI's
   *  code scan, not by the browser — an unmounted page is not proof of
   *  removal). Never auto-deleted. */
  orphaned: boolean;
  /** The trash action: fade the whole mark; the handler removes the variant
   *  code, keeps the existing/default rendering, and removes the mark. */
  markedForRemoval: boolean;
  /** THE current selection — never null. Defaults to the `existing` sentinel;
   *  selecting an option changes the live rendering immediately; exactly one
   *  option is current at all times. */
  selectedOptionName: string;
  /** The separate durable decision (v1's `locked`: "bake this"). Null = no
   *  decision yet. Keys the lifecycle (Picked); never drives rendering and
   *  never competes with or obscures `selectedOptionName`. */
  decisionOptionName: string | null;
  /** The `existing` rendering as a stored first-class option — always first
   *  in the mark's choice group, owning controls / label / comment / scoped
   *  requests like any generated option. Its `name` is always the sentinel. */
  existingOption: TweakOption;
  /** First-class typed work records scoped to the mark or to one option. */
  requests: TweakRequest[];
  /** The item's own dialogue history — owner notes, handler comments,
   *  disputes. */
  conversation: TweakConversationEntry[];
  /** Registered options in registration order (recursive tree); orphans
   *  appended (inside their surviving group where one still matches, at top
   *  level otherwise). Never contains the `existing` leaf. */
  options: TweakOptionNode[];
  /** Epoch milliseconds. */
  createdAt: number;
  /** Epoch milliseconds — bumped whenever anything on the item changes. */
  updatedAt: number;
}

/** The whole store for one context — the JSON file's root. */
export interface TweakContextFile {
  version: typeof TWEAKS_SCHEMA_VERSION;
  context: string;
  /** Epoch milliseconds of the last write. */
  updatedAt: number;
  marks: TweakMark[];
}

// ---------------------------------------------------------------------------
// Registration wire shape (hook → dev-server store, normalized from the spec)
// ---------------------------------------------------------------------------

export interface TweaksRegistrationOption {
  kind: TweakOptionNodeKind.Option;
  option: string;
}

export interface TweaksRegistrationGroup {
  kind: TweakOptionNodeKind.Group;
  entries: TweaksRegistrationEntry[];
}

export type TweaksRegistrationEntry =
  | TweaksRegistrationOption
  | TweaksRegistrationGroup;

export interface TweaksRegistrationMark {
  name: string;
  entries: TweaksRegistrationEntry[];
}

// ---------------------------------------------------------------------------
// Reveal metadata (census F08) — registration/provider only, never stored
// ---------------------------------------------------------------------------

/**
 * Where a mark's options can actually be SEEN, and how to get there — carried
 * at registration, broadcast through the beacon presence roster, never
 * persisted. `onReveal` is optional on purpose, and its absence is the useful
 * half of the contract: a mark that genuinely has nowhere to go says so
 * instead of offering a button that lands somewhere plausible.
 */
export interface TweakMarkReveal {
  /** What the trigger promises, in the host's own vocabulary — read before
   *  clicking, so it names the destination rather than the action. */
  label: string;
  /** Puts the page into the state where the options differ. Omitted where no
   *  such state exists; the row then renders inert and explains itself. */
  onReveal?: () => void;
  /** The last mile a click cannot drive — one sentence, always literal. */
  hint?: string;
}

/** Per-mark reveal metadata for one `useTweaks` call — keys are mark names. */
export type TweaksRevealSpec = Readonly<Record<string, TweakMarkReveal>>;

// ---------------------------------------------------------------------------
// Store operations — wire payloads over the `/dev/tweaks/context-state` seam
// ---------------------------------------------------------------------------

/** Store operations accepted by the `/dev/tweaks/context-state` seam.
 *  `Register` is the hook sync; every other value is a page/CLI write
 *  operation. Image upload rides the same POST endpoint as a multipart
 *  request (no `op` field — the content type is the discriminant). */
export enum TweaksStoreOp {
  Register = "register",
  /** Mark-as-read → state Seen. */
  MarkSeen = "mark-seen",
  /** THE selection: make one option (or the existing sentinel) current.
   *  Changes the live rendering immediately; never a state transition. */
  SetSelection = "set-selection",
  /** The durable decision ("bake this"): an option name / the existing
   *  sentinel (state → Picked, and the decided option becomes the current
   *  selection so the owner is looking at what he decided), or null to clear
   *  the decision (state → Seen; selection untouched). Optionally carries a
   *  pick-with-changes request in the same motion — recorded as an OPEN
   *  variants request scoped to the decided option. */
  SetDecision = "set-decision",
  /** The trash action on the whole mark (fade, queue for handler removal). */
  SetMarkRemoval = "set-mark-removal",
  /** The X on a single option (any leaf, `existing` included). */
  SetOptionRemoval = "set-option-removal",
  /** Add a typed issue/variants request scoped to the mark or one option. */
  AddRequest = "add-request",
  /** Add an option-scoped save-to-collection/system request. */
  AddSaveRequest = "add-save-request",
  /** Edit a saved request's fields (text/kind/count/…); id and createdAt are
   *  immutable. */
  EditRequest = "edit-request",
  /** Delete a saved request outright. */
  DeleteRequest = "delete-request",
  /** Open/resolve a request. */
  SetRequestStatus = "set-request-status",
  /** Dispute a fulfilled item: append the new request text to the
   *  conversation, return it to Active. */
  Dispute = "dispute",
  SetControlValue = "set-control-value",
  SetControlLocked = "set-control-locked",
  SetControlComment = "set-control-comment",
  /** Request variants on a collection item (v2 collection store). */
  CollectionAddRequest = "collection-add-request",
  /** Name a /dev route the way the owner calls it (null clears the alias). */
  SetDevPageAlias = "set-dev-page-alias",
  /** Float a /dev route to the top of the index. */
  SetDevPagePinned = "set-dev-page-pinned",
  /** Ask something of the dev-page index: a change on a page, a page's
   *  removal, or a page that does not exist yet. */
  AddDevPageRequest = "add-dev-page-request",
  /** The handler closing one of those, optionally with its answer. */
  ResolveDevPageRequest = "resolve-dev-page-request",
  /** The owner withdrawing one. */
  DeleteDevPageRequest = "delete-dev-page-request",
}

/** Owner-authored request text as submitted from a page textfield: the text
 *  plus the repo-relative paths of any pasted/dropped images (already stored
 *  through the upload seam). The store stamps timestamps on write. */
export interface TweakRequestInput {
  text: string;
  images: string[];
}

/** The composable request kinds — a save request has its own operation. */
export type TweakComposedRequestKind =
  | TweakRequestKind.Issue
  | TweakRequestKind.Variants;

export interface TweaksMarkSeenOpPayload {
  context: string;
  mark: string;
}

export interface TweaksSetSelectionOpPayload {
  context: string;
  mark: string;
  /** A leaf option name or the existing sentinel — never null. */
  option: string;
}

export interface TweaksSetDecisionOpPayload {
  context: string;
  mark: string;
  /** A leaf option name / the existing sentinel, or null to clear. */
  option: string | null;
  /** Pick-with-changes: the request riding on the decision; null = a plain
   *  decision. Only valid with a non-null option. */
  changeRequest: TweakRequestInput | null;
  /** Minimum variant count for the riding change request; defaults to
   *  `TWEAKS_DEFAULT_MIN_VARIANTS`. */
  minVariants?: number;
}

export interface TweaksSetMarkRemovalOpPayload {
  context: string;
  mark: string;
  markedForRemoval: boolean;
}

export interface TweaksSetOptionRemovalOpPayload {
  context: string;
  mark: string;
  option: string;
  markedForRemoval: boolean;
}

export interface TweaksAddRequestOpPayload {
  context: string;
  mark: string;
  kind: TweakComposedRequestKind;
  /** Option name the request belongs to; null = the whole mark. */
  option: string | null;
  request: TweakRequestInput;
  /** Variants requests only; defaults to `TWEAKS_DEFAULT_MIN_VARIANTS`. */
  minVariants?: number;
}

export interface TweaksAddSaveRequestOpPayload {
  context: string;
  mark: string;
  /** The option being saved — required. */
  option: string;
  target: TweakSaveTarget;
  displayName: string;
  description: string;
  images: string[];
  provenance?: TweakSaveProvenance;
}

/** Editable request fields. Absent = unchanged. `kind` may switch between
 *  Issue and Variants only; a Save request keeps its kind. */
export interface TweaksEditRequestOpPayload {
  context: string;
  mark: string;
  id: string;
  kind?: TweakComposedRequestKind;
  text?: string;
  images?: string[];
  minVariants?: number;
  target?: TweakSaveTarget;
  displayName?: string;
  description?: string;
}

export interface TweaksDeleteRequestOpPayload {
  context: string;
  mark: string;
  id: string;
}

export interface TweaksSetRequestStatusOpPayload {
  context: string;
  mark: string;
  id: string;
  status: TweakRequestStatus;
}

export interface TweaksDisputeOpPayload {
  context: string;
  mark: string;
  request: TweakRequestInput;
}

interface TweaksControlOpTarget {
  context: string;
  mark: string;
  option: string;
  control: string;
}

export interface TweaksSetControlValueOpPayload extends TweaksControlOpTarget {
  value: TweakControlValue;
}

export interface TweaksSetControlLockedOpPayload extends TweaksControlOpTarget {
  locked: boolean;
}

export interface TweaksSetControlCommentOpPayload
  extends TweaksControlOpTarget {
  comment: TweakCommentText | null;
}

export interface TweaksCollectionAddRequestOpPayload {
  slug: string;
  request: TweakRequestInput;
}

/** What a dev-page request asks for. A page is a thing the owner can ask to
 *  change, to remove, or to bring into existence, so the index carries the same
 *  kind of typed work a mark does. Lives here, not in the dev-page module: that
 *  module reads the filesystem, and this value has to reach the browser. */
export enum DevPageRequestKindOptions {
  Issue = "issue",
  Removal = "removal",
  Creation = "creation",
}

// ---------------------------------------------------------------------------
// Collection v2 — the greenfield collection store surface
// ---------------------------------------------------------------------------

/** Collection item slugs mirror the context-name shape (file-safe slugs). */
export const TWEAKS_COLLECTION_SLUG_PATTERN = TWEAKS_CONTEXT_NAME_PATTERN;

/** One saved collection component's store record. The component itself is
 *  code (the collection registry); the store carries only the workflow data —
 *  the item's own conversation of variant requests and handler answers, the
 *  same per-item history pattern marks use. */
export interface TweakCollectionItem {
  slug: string;
  conversation: TweakConversationEntry[];
  /** Epoch milliseconds. */
  createdAt: number;
  /** Epoch milliseconds. */
  updatedAt: number;
}

/** The whole v2 collection store — ONE JSON file at
 *  `apps/fe/src/app/dev/tweaks/collection/collection.json`. Items appear on
 *  first request; the registry (code) is the population's source of truth. */
export interface TweakCollectionFile {
  version: typeof TWEAKS_SCHEMA_VERSION;
  /** Epoch milliseconds of the last write. */
  updatedAt: number;
  items: TweakCollectionItem[];
}

// ---------------------------------------------------------------------------
// Image upload seam (requirement #19)
// ---------------------------------------------------------------------------

/** Where uploaded textfield images live, as the repo-relative prefix every
 *  stored `images` path starts with. */
export const TWEAKS_IMAGES_REPO_RELATIVE_DIR =
  "apps/fe/src/app/dev/tweaks/images";

// ---------------------------------------------------------------------------
// Hook-facing spec shape (the ruled verbatim API, recursive per census F05)
// ---------------------------------------------------------------------------

/** One entry of a mark's option list: a string is an option; a nested array
 *  is a variant sub-group — and sub-groups nest to arbitrary depth. */
export type TweaksMarkSpecEntry = string | readonly TweaksMarkSpecEntry[];

/** One mark's option list. */
export type TweaksMarkSpec = readonly TweaksMarkSpecEntry[];

/** `useTweaks(contextName, spec)` — keys are mark names. */
export type TweaksSpec = Readonly<Record<string, TweaksMarkSpec>>;

/** Result keys claimed by the hook itself — option names may not use them. */
export const RESERVED_TWEAKS_RESULT_KEYS: readonly string[] = [
  "markRef",
  "controlValue",
];

export interface NormalizedTweaksSpec {
  marks: TweaksRegistrationMark[];
  /** Human-readable registration problems (reserved/duplicate/empty names,
   *  empty groups). A mark with problems is dropped from `marks` and
   *  reported here. */
  problems: string[];
}

/** All leaf option names under one registration entry list, in spec order. */
export function registrationEntryLeafNames(
  entries: TweaksRegistrationEntry[],
): string[] {
  return entries.flatMap((entry) =>
    entry.kind === TweakOptionNodeKind.Option
      ? [entry.option]
      : registrationEntryLeafNames(entry.entries),
  );
}

/** All leaf option names of one registration mark, in spec order. */
export function registrationLeafNames(mark: TweaksRegistrationMark): string[] {
  return registrationEntryLeafNames(mark.entries);
}

/** All leaf options under a stored option tree, in stored order. Never
 *  includes the `existing` leaf — use `storedMarkLeafOptions` for the full
 *  choice set. */
export function storedTreeLeafOptions(
  entries: TweakOptionNode[],
): TweakOption[] {
  return entries.flatMap((entry) =>
    entry.kind === TweakOptionNodeKind.Option
      ? [entry.option]
      : storedTreeLeafOptions(entry.entries),
  );
}

/** The mark's full choice set: the `existing` option first (its ruled place
 *  in the group), then every registered/orphaned leaf in stored order. */
export function storedMarkLeafOptions(mark: TweakMark): TweakOption[] {
  return [mark.existingOption, ...storedTreeLeafOptions(mark.options)];
}

/** Registration-shape problems for one mark (empty/reserved/duplicate option
 *  names, empty groups). Shared by the hook's normalization and the store
 *  seam's server-side validation. */
export function collectRegistrationMarkProblems(
  mark: TweaksRegistrationMark,
): string[] {
  const markName = mark.name;
  const leafNames = registrationLeafNames(mark);
  const problems: string[] = [];
  if (!markName.trim()) {
    problems.push("A mark name is empty.");
  }
  if (hasEmptyGroup(mark.entries)) {
    problems.push(`Mark "${markName}" contains an empty option group.`);
  }
  const seenNames = new Set<string>();
  for (const leafName of leafNames) {
    if (!leafName.trim()) {
      problems.push(`Mark "${markName}" has an empty option name.`);
    } else if (leafName === (TweakOptionSentinel.Existing as string)) {
      problems.push(
        `Mark "${markName}" registers the reserved option name "${leafName}" — the existing option is store-created, never registered.`,
      );
    } else if (RESERVED_TWEAKS_RESULT_KEYS.includes(leafName)) {
      problems.push(
        `Mark "${markName}" registers "${leafName}", a key reserved by the useTweaks result.`,
      );
    } else if (seenNames.has(leafName)) {
      problems.push(
        `Mark "${markName}" registers the option name "${leafName}" twice.`,
      );
    }
    seenNames.add(leafName);
  }
  return problems;
}

function hasEmptyGroup(entries: TweaksRegistrationEntry[]): boolean {
  return entries.some(
    (entry) =>
      entry.kind === TweakOptionNodeKind.Group &&
      (entry.entries.length === 0 || hasEmptyGroup(entry.entries)),
  );
}

function specEntryToRegistrationEntry(
  item: TweaksMarkSpecEntry,
): TweaksRegistrationEntry {
  return typeof item === "string"
    ? { kind: TweakOptionNodeKind.Option, option: item }
    : {
        kind: TweakOptionNodeKind.Group,
        entries: item.map(specEntryToRegistrationEntry),
      };
}

/** Normalize the ruled spec shape into the registration wire shape, dropping
 *  (and reporting) marks with invalid names. Pure — shared by the hook, the
 *  dev-server seam, and the CLI. */
export function normalizeTweaksSpec(spec: TweaksSpec): NormalizedTweaksSpec {
  const marks: TweaksRegistrationMark[] = [];
  const problems: string[] = [];
  const claimedLeafNames = new Set<string>();

  for (const [markName, markSpec] of Object.entries(spec)) {
    const entries: TweaksRegistrationEntry[] = markSpec.map(
      specEntryToRegistrationEntry,
    );
    const mark: TweaksRegistrationMark = { name: markName, entries };
    const leafNames = registrationLeafNames(mark);
    const markProblems = collectRegistrationMarkProblems(mark);
    const crossMarkCollisions = leafNames.filter((leafName) =>
      claimedLeafNames.has(leafName),
    );
    markProblems.push(
      ...crossMarkCollisions.map(
        (leafName) =>
          `Option name "${leafName}" (mark "${markName}") is already used by another mark in this call — result keys are flat.`,
      ),
    );
    if (markProblems.length > 0) {
      problems.push(...markProblems);
      continue;
    }
    leafNames.forEach((leafName) => claimedLeafNames.add(leafName));
    marks.push(mark);
  }

  return { marks, problems };
}
